import { inject, Injectable } from '@angular/core';
import { MediaMapper } from '../mappers/media.mapper';
import { Media } from '../models/media.model';
import { IPaginatedItemsModel } from '@shared/sync/paginated-items.model';
import { HsBlazorService } from '@hsetu/core-services';

@Injectable({
  providedIn: 'root',
})
export class MediaService {
  private readonly _serviceKey = 'kfmMedia';
  private readonly _blazorService = inject(HsBlazorService);
  private readonly _mapper = new MediaMapper();

  public async getByOwner(
    ownerId: string,
    pageSize = 10,
    pageIndex = 0,
  ): Promise<IPaginatedItemsModel<Media>> {
    const sortModel = {
      sort: 0, // 0 = ASC, 1 = DESC
      property: 'stampCreate',
    };

    const filterModel = {
      filterStatement: `return i.ownerKey == p[0]`,
      parameters: [ownerId],
    };

    const mediaResult = await this._blazorService.invokeService(
      this._serviceKey,
      'GetByOwner',
      {
        pageIndex: pageIndex,
        pageSize: pageSize,
        sortBy: sortModel,
        filterBy: filterModel,
      },
    );

    if (mediaResult) {
      return (mediaResult as Record<string, IPaginatedItemsModel<Media>>)[
        'result'
      ];
    }

    return { count: 0, data: [], pageSize: 0, pageIndex: 0 };
  }

  public async insertOrUpdate(value: Media): Promise<Media | undefined> {
    const request = this._mapper.mapPutMediaRequest(value);
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Put',
      request,
    );

    if (result) {
      const id = (result as Record<string, string | undefined>)['id'];
      value.uId = id ? id : value.uId;
      return value;
    }

    return undefined;
  }

  public async createMultiple(values: Media[]): Promise<Media[] | undefined> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Create',
      { media: values },
    );

    if (result) {
      const media = (result as Record<string, unknown>)['media'];
      if (media && media instanceof Array) {
        return media as Media[];
      }
    }

    return undefined;
  }

  public async delete(id: string): Promise<boolean> {
    const result = await this._blazorService.invokeService(
      this._serviceKey,
      'Delete',
      { id: id },
    );

    return !!result && (result as Record<string, unknown>)['deleted'] === true;
  }
}
